import { format, parse, differenceInMinutes } from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import ct from 'countries-and-timezones';
import { TimeZone } from '@marageti/z4-sdk/lib/standards';
import { AvailabilityLimitType, Partner } from '@marageti/z4-sdk/lib/travel';
import { FieldErrors } from 'react-hook-form';

export const getTargetTimezone = (loggedInAgent: any, partner: Partner | undefined): { name: string; offsetMinutes: number } => {
  let targetTimezoneName: string | undefined;
  let targetTimezoneOffsetMinutes: number | undefined;

  if (loggedInAgent?.timeZone) {
    targetTimezoneName = (loggedInAgent.timeZone as TimeZone).name;
    targetTimezoneOffsetMinutes = (loggedInAgent.timeZone as TimeZone).rawOffsetInMinutes;
  }

  if (!targetTimezoneName && partner?.offices && partner.offices.length > 0) {
    const office = partner.offices[0];
    if (office.country && office.city) {
      const countryInfo = ct.getCountry(office.country);
      if (countryInfo) {
        const normalizedCity = office.city.replace(/\s+/g, '_').toLowerCase();
        targetTimezoneName = countryInfo.timezones.find((tz) => tz.toLowerCase().includes(normalizedCity));
        if (targetTimezoneName) {
          targetTimezoneOffsetMinutes = ct.getTimezone(targetTimezoneName)?.utcOffset;
        }
      }
    }
  }

  if (!targetTimezoneName) {
    targetTimezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    targetTimezoneOffsetMinutes = ct.getTimezone(targetTimezoneName)?.utcOffset;
  }

  return { name: targetTimezoneName, offsetMinutes: targetTimezoneOffsetMinutes || 0 };
};

export const toIANAFormat = (timezoneName: string): string => timezoneName.replace(/ /g, '_');

export const getStartOfDayInTimezone = (date: Date, timezoneOffsetInMinutes: number): string => {
  const dateStr = format(date, 'yyyy-MM-dd');
  const parsed = parse(dateStr, 'yyyy-MM-dd', new Date());
  const startOfDayUtc = Date.UTC(
    parsed.getFullYear(),
    parsed.getMonth(),
    parsed.getDate(),
    0,
    0,
    0,
    0,
  );
  return new Date(startOfDayUtc - timezoneOffsetInMinutes * 60 * 1000).toISOString();
};

export const getEndOfDayInTimezone = (date: Date, timezoneOffsetInMinutes: number): string => {
  const dateStr = format(date, 'yyyy-MM-dd');
  const parsed = parse(dateStr, 'yyyy-MM-dd', new Date());
  const endOfDayUtc = Date.UTC(
    parsed.getFullYear(),
    parsed.getMonth(),
    parsed.getDate(),
    23,
    59,
    59,
    999,
  );
  return new Date(endOfDayUtc - timezoneOffsetInMinutes * 60 * 1000).toISOString();
};

export const getInitialDate = (dateStr: string | undefined, timezoneName?: string): Date => {
  if (!dateStr) return new Date();
  const tz = toIANAFormat(timezoneName || 'UTC');
  const zoned = utcToZonedTime(new Date(dateStr), tz);
  return new Date(zoned.getFullYear(), zoned.getMonth(), zoned.getDate());
};

const formatUtcOffset = (offsetMinutes: number): string => {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;
  return minutes > 0
    ? `UTC${sign}${hours}:${minutes.toString().padStart(2, '0')}`
    : `UTC${sign}${hours}`;
};

const getTimezoneDisplayName = (timezoneName: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: toIANAFormat(timezoneName),
      timeZoneName: 'long',
    });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value ?? timezoneName;
    return tzPart.replace(/ (Standard|Daylight) Time$/, ' Time');
  } catch {
    return timezoneName;
  }
};

export const getTimezoneMismatchWarning = (
  startDateStr: string | undefined,
  timezoneName: string,
  currentOffsetMinutes?: number,
): string | null => {
  if (!startDateStr) return null;

  const instant = new Date(startDateStr);
  const ianaTimezone = toIANAFormat(timezoneName);
  const zoned = utcToZonedTime(instant, ianaTimezone);
  const startOfDayInZone = zonedTimeToUtc(
    new Date(zoned.getFullYear(), zoned.getMonth(), zoned.getDate(), 0, 0, 0, 0),
    ianaTimezone,
  );
  const timeDiff = Math.abs(differenceInMinutes(instant, startOfDayInZone));

  if (timeDiff > 0) {
    const utcMinutes = instant.getUTCHours() * 60 + instant.getUTCMinutes();
    let originalOffsetMinutes = (1440 - utcMinutes) % 1440;
    if (originalOffsetMinutes > 720) originalOffsetMinutes -= 1440;

    const currentTz = ct.getTimezone(ianaTimezone);
    const resolvedCurrentOffset = currentOffsetMinutes ?? currentTz?.utcOffset ?? 0;

    const originalOffsetStr = formatUtcOffset(originalOffsetMinutes);
    const currentOffsetStr = formatUtcOffset(resolvedCurrentOffset);
    const currentDisplayName = getTimezoneDisplayName(timezoneName);

    return `This restriction was originally set in a different timezone (${originalOffsetStr}). You're now editing it in ${currentDisplayName} (${currentOffsetStr}).`;
  }
  return null;
};

export const validateLeadCapacity = (value: any, partner: Partner | undefined) => {
  const numValue = Number(value);
  if (Number.isNaN(numValue)) return 'Please enter a valid number';
  if (numValue < 0) return 'Please enter a number greater than or equal to 0';
  if (partner?.capacity && numValue >= partner.capacity) return `Must be less than the current Max Capacity of ${partner.capacity}`;
  return true;
};

export const getHelperText = (
  errors: FieldErrors,
  selectedType: AvailabilityLimitType | null | undefined,
  partner: Partner | undefined,
) => {
  if (errors.weeklyCapacity) {
    return errors.weeklyCapacity.message as string;
  }
  if (selectedType === AvailabilityLimitType.LIMIT_TRAVEL) {
    return 'Limit Trips Type required to set Trip Capacity';
  }
  return `Must be less than the current Max Capacity of ${partner?.capacity}`;
};
